import { useEffect } from "react";
import { Outlet, createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { Button } from "@typedoc/ui";
import { ErrorNotice } from "../components/error-notice";
import { PendingScreen } from "../components/pending-screen";
import { RouteError } from "../components/route-error";
import type { AppError } from "../lib/app-error";
import { authClient } from "../lib/auth-client";
import { confirmSession } from "../lib/session-gate";

/** A session read that failed, as distinct from any other failure below this route. */
class SessionUnavailable extends Error {
  readonly appError: AppError;

  constructor(appError: AppError) {
    super(appError.message);
    this.appError = appError;
  }
}

/**
 * Every screen that needs a proven session sits under this route, and none of it renders until
 * the session is known. Deciding in `beforeLoad` rather than in an effect is the point: an effect
 * runs after the page has painted, so a signed-out visitor saw the signed-in screen before being
 * sent away from it.
 */
export const Route = createFileRoute("/_authenticated")({
  beforeLoad: async () => {
    const check = await confirmSession();
    if (check.status === "signed-out") throw redirect({ to: "/sign-in", replace: true });
    if (check.status === "unverified") throw redirect({ to: "/verify-email", replace: true });
    // A failed read is not "signed out": sending everyone to sign in during an outage would
    // offer a form that cannot work. It stays here, says so, and can be asked again.
    if (check.status === "failed") throw new SessionUnavailable(check.error);
    return { session: check.session };
  },
  pendingComponent: () => <PendingScreen label="Getting your workspace ready…" />,
  errorComponent: SessionError,
  component: Authenticated,
});

function Authenticated() {
  const router = useRouter();
  const { data, isPending, isRefetching, error } = authClient.useSession();
  // The session can end while a screen is open — signed out in another tab, or expired. Run the
  // same check again rather than deciding here, so there is one rule for where that leads.
  const ended = !data && !isPending && !isRefetching && !error;
  useEffect(() => {
    if (ended) void router.invalidate();
  }, [ended, router]);
  return <Outlet />;
}

function SessionError({ error, reset }: { error: unknown; reset: () => void }) {
  const router = useRouter();
  if (!(error instanceof SessionUnavailable)) return <RouteError onRetry={reset} />;
  return (
    <main id="main" className="pending-screen">
      <div className="session-error">
        <h1>We couldn't check your session</h1>
        <ErrorNotice error={error.appError} variant="destructive" />
        <Button variant="outline" onClick={() => void router.invalidate()}>
          Try again
        </Button>
      </div>
    </main>
  );
}
